import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Truck, ChevronLeft, ChevronRight, CheckCircle2, Ban, Printer, FileText, Receipt, Search, X, Loader2, Plus, Phone } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { Tooltip } from '@/components/ui/Tooltip';
import { useToast } from '@/components/ui/ToastContext';
import { useSipPhoneContext } from '@/contexts/SipPhoneContext';
import { supabase } from '@/lib/supabaseClient';
import { fetchFullInvoiceForOrderAdmin, type FullInvoiceForOrderAdmin } from '@/lib/invoiceApi';
import { waitForImages } from '@/lib/waitForImages';
import { InvoiceDocument } from '@/components/invoices/InvoiceDocument';
import { ReceiptDocument } from '@/components/invoices/ReceiptDocument';
import { printInvoicePdf, willFitOnOnePage } from '@/lib/downloadInvoicePdf';
import { DeliveryChallanDocument, type DeliveryChallanData } from '@/components/invoices/DeliveryChallanDocument';
import { PathaoLocationModal } from '@/components/orders/PathaoLocationModal';
import type { PathaoLocation } from '@/components/orders/PathaoLocationModal';
import { ShipModal } from '@/components/orders/ShipModal';
import type { ShipSubmission } from '@/components/orders/ShipModal';
import { NewOrderModal } from '@/components/orders/NewOrderModal';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

type DocFormat = 'a4' | 'pos' | 'challan';

// 'dispatched'/'cod_collected'/'remitted'/'settled' were listed here but no
// code path ever sets core_orders.status to any of them (COD reconciliation
// lives on core_shipments.cod_status + core_fin_ledger instead) — they'd
// always match zero orders, so they're dropped from the filter.
const STATUSES = ['placed', 'confirmed', 'shipped', 'delivered', 'cancelled', 'returned'];
const STATUS_TONE: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  placed: 'primary',
  confirmed: 'primary',
  shipped: 'warning',
  dispatched: 'warning',
  delivered: 'success',
  cod_collected: 'success',
  remitted: 'success',
  settled: 'success',
  cancelled: 'error',
  returned: 'error',
};
const PAGE_SIZE = 20;

// A walk-in sale or a gift handed over/mailed without a courier still ends
// at status 'delivered' internally (so Customer Followup scheduling and
// reports keep working the same way as a real courier delivery) — but the
// badge says "picked up" instead, so staff never read it as "a courier
// confirmed this" when no courier was ever involved.
function orderStatusLabel(o: { status: string; requires_delivery: boolean }): string {
  if (o.status === 'delivered' && !o.requires_delivery) return 'picked up';
  return o.status;
}

interface OrderRow {
  id: string;
  order_no: string;
  status: string;
  total_bdt: number;
  placed_at: string;
  customer_id: string | null;
  customer_info: { name?: string; phone?: string; address?: string } | null;
  cancellation_reason: string | null;
  is_gift: boolean;
  channel: string;
  partner_id: string | null;
  requires_delivery: boolean;
  seller_ref: string | null;
}

const PICKK_CHANNELS = ['pickk_seller', 'pickk_gb'];
const CHANNEL_TABS: { key: 'all' | 'rxk_web' | 'store' | 'pharmacy_b2b' | 'pickk'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'rxk_web', label: 'Online' },
  { key: 'store', label: 'Store' },
  { key: 'pharmacy_b2b', label: 'Partner' },
  { key: 'pickk', label: 'Pick' },
];

interface PartnerOption {
  id: string;
  name: string;
  type: string;
  status: string;
}

// Inline A4/POS picker on each order row — selecting a format prints right
// here on the Orders list (via a hidden print-only node), so the admin
// never leaves this page to print.
function OrderPrintMenu({ orderId, onPrint }: { orderId: string; onPrint: (orderId: string, format: DocFormat) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const goPrint = (format: DocFormat) => {
    setOpen(false);
    onPrint(orderId, format);
  };

  return (
    <div className="relative" ref={ref}>
      <Button size="sm" variant="ghost" title="Print Invoice" onClick={() => setOpen((o) => !o)}>
        <Printer className="w-3.5 h-3.5" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-20 bg-white rounded-lg border border-border-subtle shadow-modal overflow-hidden z-50">
          <button
            onClick={() => goPrint('a4')}
            className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-ink hover:bg-surface-muted cursor-pointer"
          >
            <FileText className="w-3 h-3 text-ink-faint" /> A4
          </button>
          <button
            onClick={() => goPrint('pos')}
            className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-ink hover:bg-surface-muted cursor-pointer border-t border-border-subtle"
          >
            <Receipt className="w-3 h-3 text-ink-faint" /> POS
          </button>
          {/* No remarks prompt here (unlike the full invoice page) — this is
              the quick-print path, so Challan just prints with blank
              remarks; open the order's own Invoice page to add one. */}
          <button
            onClick={() => goPrint('challan')}
            className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-ink hover:bg-surface-muted cursor-pointer border-t border-border-subtle"
          >
            <Truck className="w-3 h-3 text-ink-faint" /> Challan
          </button>
        </div>
      )}
    </div>
  );
}

export default function RxkOrders() {
  const navigate = useNavigate();
  const { push } = useToast();
  const phone = useSipPhoneContext();

  const callNumber = (number: string, orderId: string) => {
    if (phone.registrationStatus !== 'registered') {
      push('error', phone.hasCredentials ? 'Phone is not connected yet — check the status in Communications.' : 'No SIP extension set up for you yet — ask an admin to set one up in ERP.');
      return;
    }
    phone.call(number, orderId);
  };

  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', dateFrom: '', dateTo: '' });
  const [channelTab, setChannelTab] = useState<'all' | 'rxk_web' | 'store' | 'pharmacy_b2b' | 'pickk'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [pathaoModalOrder, setPathaoModalOrder] = useState<OrderRow | null>(null);
  const [pathaoBusy, setPathaoBusy] = useState(false);
  const [shipModalOrder, setShipModalOrder] = useState<OrderRow | null>(null);
  const [shipBusy, setShipBusy] = useState(false);
  const [cancelModalOrder, setCancelModalOrder] = useState<OrderRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [printJob, setPrintJob] = useState<{ orderId: string; format: DocFormat } | null>(null);
  const [printData, setPrintData] = useState<FullInvoiceForOrderAdmin | null>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    // Fetched without a status filter so past reseller orders can still
    // resolve a partner's name even if that partner later goes inactive.
    supabase
      .from('core_partners')
      .select('id, name, type, status')
      .order('name')
      .then(({ data }) => setPartners((data as PartnerOption[]) ?? []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('core_orders')
      .select('id, order_no, status, total_bdt, placed_at, customer_id, customer_info, cancellation_reason, is_gift, channel, partner_id, requires_delivery, seller_ref', { count: 'exact' })
      .order('placed_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
    if (channelTab === 'all') {
      query = query.in('channel', ['rxk_web', 'pharmacy_b2b', ...PICKK_CHANNELS]);
    } else if (channelTab === 'pickk') {
      query = query.in('channel', PICKK_CHANNELS);
    } else if (channelTab === 'rxk_web') {
      // Real delivery orders only — a website order that's actually a gift
      // handed over/mailed separately, or a walk-in counter sale, never
      // goes through a courier at all (see 'store' below for those).
      query = query.eq('channel', 'rxk_web').eq('requires_delivery', true);
    } else if (channelTab === 'store') {
      query = query.eq('channel', 'rxk_web').eq('requires_delivery', false);
    } else {
      query = query.eq('channel', channelTab);
    }
    if (filters.status) query = query.eq('status', filters.status);
    if (filters.dateFrom) query = query.gte('placed_at', filters.dateFrom);
    if (filters.dateTo) query = query.lt('placed_at', `${filters.dateTo}T23:59:59.999`);
    if (debouncedSearch) {
      // Strip characters that are structurally significant to PostgREST's
      // .or() filter syntax (comma separates conditions, parens group them)
      // so a stray character in what's typed can't break or hijack the query.
      const term = debouncedSearch.replace(/[,()]/g, '');
      query = query.or(`order_no.ilike.%${term}%,customer_info->>phone.ilike.%${term}%`);
    }

    const { data, count } = await query;
    setOrders((data as OrderRow[]) ?? []);
    setTotalCount(count ?? 0);
    setLoading(false);
  }, [page, filters, debouncedSearch, channelTab]);

  // Live search: waits for a short pause in typing before actually
  // re-querying, so every keystroke doesn't fire its own request.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetches the chosen order's invoice/receipt data without navigating away
  // from this list — the document renders into a print-only hidden node
  // below, and window.print() fires once it's ready.
  useEffect(() => {
    if (!printJob) return;
    let cancelled = false;
    fetchFullInvoiceForOrderAdmin(printJob.orderId).then((data) => {
      if (cancelled) return;
      if (!data) {
        push('error', 'No invoice found for this order');
        setPrintJob(null);
        return;
      }
      setPrintData(data);
    });
    return () => {
      cancelled = true;
    };
  }, [printJob, push]);

  // Overrides the app-wide A4 @page rule (src/index.css) only while a POS
  // print job is active, so a thermal receipt prints at whatever width the
  // loaded roll actually is instead of being forced onto A4. Height is an
  // explicit, generously tall 1000mm rather than "auto" — "auto" for both
  // dimensions left the real page length up to the printer/OS, which on a
  // real thermal printer forced a page break (and a blank 2nd "page")
  // partway down a receipt that should have printed continuously. An
  // explicit height this tall gives the browser no reason to ever
  // paginate a normal receipt, while the driver still only advances/cuts
  // the roll as far as the actual content goes.
  useEffect(() => {
    if (printJob?.format !== 'pos') return;
    const style = document.createElement('style');
    style.id = 'receipt-page-size-override';
    style.textContent = '@media print { @page { size: auto 1000mm; margin: 2mm; } }';
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, [printJob?.format]);

  useEffect(() => {
    if (!printData || !printJob) return;
    let cancelled = false;
    (async () => {
      if (printContainerRef.current) await waitForImages(printContainerRef.current);
      if (cancelled) return;
      // A4 prints natively for the common single-page case (crisper than
      // any rasterized PDF) and only falls back to the same pipeline
      // Download uses when the invoice genuinely spans multiple pages —
      // native print's fixed footer can't paginate correctly there. See
      // willFitOnOnePage's own comment. POS/Challan stay on native print
      // either way.
      if (printJob.format === 'a4' && printContainerRef.current && !willFitOnOnePage(printContainerRef.current)) {
        await printInvoicePdf(printContainerRef.current);
      } else {
        window.print();
      }
      setPrintJob(null);
      setPrintData(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [printData, printJob]);

  const updateFilter = (patch: Partial<typeof filters>) => {
    setPage(0);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const changeChannelTab = (tab: typeof channelTab) => {
    setPage(0);
    setChannelTab(tab);
  };

  const handleShip = async (submission: ShipSubmission) => {
    if (!shipModalOrder) return;
    setShipBusy(true);
    const { error } = await supabase.from('core_shipments').insert({
      order_id: shipModalOrder.id,
      courier: submission.courierName,
      courier_partner_id: submission.courierPartnerId,
      tracking_no: submission.trackingNo,
      booked_via: 'manual',
      dispatched_at: new Date().toISOString(),
    });
    if (!error) await supabase.from('core_orders').update({ status: 'shipped' }).eq('id', shipModalOrder.id);
    setShipBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Shipment created (배송 등록됨)');
    setShipModalOrder(null);
    load();
  };

  const handleConfirm = async (o: OrderRow) => {
    setConfirmingId(o.id);
    try {
      if (!o.requires_delivery) {
        // Walk-in sale (or a gift handed over / mailed separately) — no
        // courier is ever involved, so there's nothing to book or ship.
        // Goes straight through to 'delivered' in the same click (rather
        // than sitting at 'confirmed' forever with no way to close out),
        // which also schedules its Customer Followup like any other
        // delivered order.
        const { error: confirmErr } = await supabase.rpc('core_advance_order', { p_order_id: o.id, p_next: 'confirmed' });
        if (confirmErr) throw confirmErr;
        const { error: deliverErr } = await supabase.rpc('core_advance_order', { p_order_id: o.id, p_next: 'delivered' });
        if (deliverErr) throw deliverErr;
        push('success', 'Order confirmed');
        load();
        return;
      }
      const { data, error } = await supabase.functions.invoke('core-book-shipment', { body: { order_id: o.id } });
      if (error) throw error;
      if (data?.already_booked) {
        push('success', `Already booked — tracking ${data.tracking_no}`);
      } else if (data?.booked) {
        push('success', `Confirmed & auto-shipped via ${data.courier} — tracking ${data.tracking_no}`);
      } else if (data?.error === 'pathao_location_required') {
        setPathaoModalOrder(o);
        return; // waiting on the picker — don't reload yet
      } else if (data?.confirmed) {
        push('error', `Order confirmed, but auto-booking failed: ${data.details ?? data.error}. Ship manually or fix courier settings.`);
      } else {
        push('error', data?.details ?? data?.error ?? 'Failed to confirm order');
      }
      load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to confirm order');
    } finally {
      setConfirmingId(null);
    }
  };

  const handlePathaoLocationSubmit = async (location: PathaoLocation) => {
    if (!pathaoModalOrder) return;
    setPathaoBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('core-book-shipment', { body: { order_id: pathaoModalOrder.id, pathao_location: location } });
      if (error) throw error;
      if (data?.booked) {
        push('success', `Confirmed & auto-shipped via Pathao — tracking ${data.tracking_no}`);
      } else {
        push('error', `Pathao booking failed: ${data?.details ?? data?.error}. Ship manually or retry.`);
      }
      setPathaoModalOrder(null);
      load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to book with Pathao');
    } finally {
      setPathaoBusy(false);
    }
  };

  const handleCancelOrder = async (e: FormEvent) => {
    e.preventDefault();
    if (!cancelModalOrder || !cancelReason.trim()) return;
    setCancelBusy(true);
    const { error: advanceErr } = await supabase.rpc('core_advance_order', { p_order_id: cancelModalOrder.id, p_next: 'cancelled' });
    if (!advanceErr) {
      await supabase.from('core_orders').update({ cancellation_reason: cancelReason.trim() }).eq('id', cancelModalOrder.id);
    }
    setCancelBusy(false);
    if (advanceErr) {
      push('error', advanceErr.message);
      return;
    }
    push('success', 'Order cancelled (주문 취소됨)');
    setCancelModalOrder(null);
    setCancelReason('');
    load();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const quickChallanRequest: DeliveryChallanData | null =
    printData && printJob?.format === 'challan'
      ? {
          challan_number: printData.request.doc_number?.replace(/^INV-/, 'CHA-') ?? null,
          issue_date: printData.request.issue_date,
          company_name: printData.request.company_name || printData.request.recipient_name,
          contact_person: printData.receivedBy.contactPerson,
          contact_designation: printData.receivedBy.designation,
          mobile: printData.request.company_contact,
          address: printData.request.company_address,
          items: printData.request.line_items.map((item) => ({ description: item.description, unit: 'Pcs', quantity: item.quantity, remarks: '' })),
        }
      : null;

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5 print:hidden">
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-bold text-ink">Orders (주문)</h1>
          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus className="w-4 h-4" /> New Order
          </Button>
        </div>

        <Card>
          <CardBody className="flex flex-wrap items-end gap-3 py-3">
            <Field label="Search">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-ink-faint absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Order number or phone…"
                  className="!pl-9 !pr-9 !w-72"
                />
                {loading && search && debouncedSearch === search.trim() ? (
                  <Loader2 className="w-3.5 h-3.5 text-ink-faint absolute right-3 top-1/2 -translate-y-1/2 animate-spin pointer-events-none" />
                ) : (
                  search && (
                    <button
                      onClick={() => setSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink cursor-pointer"
                      title="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )
                )}
              </div>
            </Field>
            <Field label="Status">
              <Select value={filters.status} onChange={(e) => updateFilter({ status: e.target.value })} className="!w-40">
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
            </Field>
            <Field label="From">
              <Input type="date" value={filters.dateFrom} onChange={(e) => updateFilter({ dateFrom: e.target.value })} className="!w-40" />
            </Field>
            <Field label="To">
              <Input type="date" value={filters.dateTo} onChange={(e) => updateFilter({ dateTo: e.target.value })} className="!w-40" />
            </Field>
            {(filters.status || filters.dateFrom || filters.dateTo || search) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  updateFilter({ status: '', dateFrom: '', dateTo: '' });
                  setSearch('');
                }}
              >
                Clear
              </Button>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="RxK Orders"
            subtitle={totalCount > 0 ? `${page * PAGE_SIZE + 1}–${Math.min(totalCount, (page + 1) * PAGE_SIZE)} of ${totalCount}` : undefined}
            icon={<ShoppingCart className="w-4 h-4" />}
            action={
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 rounded-lg bg-surface-muted p-1">
                  {CHANNEL_TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => changeChannelTab(t.key)}
                      className={`px-2.5 py-1 rounded-md text-[11.5px] font-semibold transition-colors cursor-pointer ${
                        channelTab === t.key ? 'bg-white text-primary-800 shadow-sm' : 'text-ink-faint hover:text-ink'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  <span className="text-[11.5px] text-ink-faint px-1">{page + 1} / {totalPages}</span>
                  <Button size="sm" variant="secondary" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            }
          />
          <CardBody className="space-y-1.5">
            {loading ? (
              <Spinner />
            ) : orders.length === 0 ? (
              <EmptyState title="No orders match these filters" />
            ) : (
              orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted">
                  <button onClick={() => navigate(`/admin/orders/${o.id}`)} className="text-left cursor-pointer flex-1 min-w-0">
                    <p className="text-[12.5px] font-semibold text-ink hover:text-primary-800">{o.order_no}</p>
                    <p className="text-[11px] text-ink-faint">
                      {o.customer_info?.name ?? 'Guest'} · ৳{o.total_bdt.toLocaleString()} · {new Date(o.placed_at).toLocaleDateString()}
                      {o.customer_id && <Badge tone="info">account</Badge>}
                    </p>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <OrderPrintMenu orderId={o.id} onPrint={(id, format) => setPrintJob({ orderId: id, format })} />
                    {o.order_no.startsWith('STR-') && <Badge tone="info">🏪 Store</Badge>}
                    {o.is_gift && <Badge tone="warning">🎁 Gift</Badge>}
                    {o.channel === 'pharmacy_b2b' && (
                      <Badge tone="primary">🏢 {partners.find((p) => p.id === o.partner_id)?.name ?? 'Reseller'}</Badge>
                    )}
                    {PICKK_CHANNELS.includes(o.channel) && (
                      <Badge tone="info">via Pick-k{o.seller_ref ? ` — ${o.seller_ref}` : ''}</Badge>
                    )}
                    {o.status === 'cancelled' && o.cancellation_reason ? (
                      <Tooltip content={o.cancellation_reason}>
                        <Badge tone={STATUS_TONE[o.status] ?? 'neutral'}>{orderStatusLabel(o)}</Badge>
                      </Tooltip>
                    ) : (
                      <Badge tone={STATUS_TONE[o.status] ?? 'neutral'}>{orderStatusLabel(o)}</Badge>
                    )}
                    {(o.status === 'placed' || o.status === 'confirmed') && (
                      <>
                        {o.customer_info?.phone && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => callNumber(o.customer_info!.phone!, o.id)}
                            title={`Call ${o.customer_info.phone}`}
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {/* Walk-in sales (and a gift handed over / mailed
                            separately) never involve a courier — nothing to
                            confirm/retry/ship. Checked via the persisted
                            requires_delivery column, not the order_no
                            prefix: a walk-in gift gets GFT- (is_gift takes
                            prefix priority over STR-), which used to hide
                            this signal entirely. */}
                        {o.requires_delivery ? (
                          <>
                            <Button size="sm" onClick={() => handleConfirm(o)} loading={confirmingId === o.id}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> {o.status === 'confirmed' ? 'Retry Booking' : 'Confirm'}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => setShipModalOrder(o)}>
                              <Truck className="w-3.5 h-3.5" /> Ship
                            </Button>
                          </>
                        ) : (
                          o.status === 'placed' && (
                            <Button size="sm" onClick={() => handleConfirm(o)} loading={confirmingId === o.id}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> Confirm
                            </Button>
                          )
                        )}
                        <Button size="sm" variant="secondary" onClick={() => setCancelModalOrder(o)}>
                          <Ban className="w-3.5 h-3.5" /> Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>

      <NewOrderModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onCreated={load} />

      <PathaoLocationModal
        open={!!pathaoModalOrder}
        onClose={() => setPathaoModalOrder(null)}
        onSubmit={handlePathaoLocationSubmit}
        busy={pathaoBusy}
        customerName={pathaoModalOrder?.customer_info?.name}
        customerPhone={pathaoModalOrder?.customer_info?.phone}
        customerAddress={pathaoModalOrder?.customer_info?.address}
      />
      <ShipModal open={!!shipModalOrder} onClose={() => setShipModalOrder(null)} onSubmit={handleShip} orderNo={shipModalOrder?.order_no ?? ''} busy={shipBusy} />

      <Modal open={!!cancelModalOrder} onClose={() => setCancelModalOrder(null)} title="Cancel Order" subtitle={cancelModalOrder?.order_no} icon={<Ban className="w-4 h-4" />} busy={cancelBusy}>
        <form onSubmit={handleCancelOrder} className="space-y-3">
          <Field label="Reason for cancellation">
            <Textarea required rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Customer requested cancellation, out of stock, duplicate order…" />
          </Field>
          <Button type="submit" variant="danger" loading={cancelBusy} disabled={!cancelReason.trim()} className="w-full">Confirm Cancel</Button>
        </form>
      </Modal>

      {/* Print-only: kept in normal flow (just pushed off-screen) rather
          than display:none, since browsers defer/skip loading <img>s inside
          a display:none subtree — that was silently dropping the signed
          logo/watermark URLs. print-only-container resets position:static
          in @media print, so this becomes the actual printed page. */}
      {printData && (
        <div className="print-only-container" style={{ position: 'fixed', top: 0, left: '-10000px' }} ref={printContainerRef}>
          <div className="mx-auto" style={{ width: 'fit-content' }}>
            {printJob?.format === 'pos' ? (
              <ReceiptDocument template={printData.template} request={printData.request} payment={printData.payment} salesPerson={printData.salesPerson} />
            ) : printJob?.format === 'challan' && quickChallanRequest ? (
              <>
                <DeliveryChallanDocument template={printData.challanTemplate} request={quickChallanRequest} copyLabel="customer" refund={printData.refund} cancelled={printData.cancelled} cancelReason={printData.cancelReason} />
                {/* Forces the Office Copy onto its own page — a plain
                    sibling div here is enough since break-after only needs
                    an in-flow box to act on. */}
                <div style={{ breakAfter: 'page', pageBreakAfter: 'always' }} />
                <DeliveryChallanDocument template={printData.challanTemplate} request={quickChallanRequest} copyLabel="office" refund={printData.refund} cancelled={printData.cancelled} cancelReason={printData.cancelReason} />
              </>
            ) : (
              <InvoiceDocument template={printData.template} request={printData.request} payment={printData.payment} salesPerson={printData.salesPerson} soldBy={printData.soldBy} refund={printData.refund} cancelled={printData.cancelled} cancelReason={printData.cancelReason} />
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
